import { useEffect, useMemo, useState } from "react";
import { DataVisualizationType } from "components/charting/config/chartTypes";
import ChartProfileSelectContext from "components/charting/contexts/ChartProfileSelectContext";
import ChartProfileSelectStatusContext from "components/charting/contexts/ChartProfileSelectStatusContext";
import useChartSelections from "components/charting/hooks/useChartSelections";
import useDataVisualizationType from "components/charting/hooks/useDataVisualizationType";
import useProfileTypeQueryState, { ProfileType } from "components/charting/hooks/useProfileTypeQueryState";
import useGetLatestLoadVelocityProfilesQuery from "hooks/useGetLatestLoadVelocityProfilesQuery";
import LoadVelocityProfileHelpers from "helpers/LoadVelocityProfileHelpers";
import type { LoadVelocityProfile } from "types/load-velocity-profile";

interface ChartProfileSelectProviderProps {
  children?: React.ReactNode;
}

function ChartProfileSelectProvider(props: ChartProfileSelectProviderProps) {
  const selections = useChartSelections();
  const visType = useDataVisualizationType();
  const [profileType] = useProfileTypeQueryState();
  const [selectedProfiles, setSelectedProfiles] = useState<LoadVelocityProfile[]>([]);
  const measure = selections.measures[0];
  const athleteId = selections.sources[0]?.id;
  const exerciseId = measure.id;

  // Only velocity profiles exist for now.
  const enableLvpApi = Boolean(
    visType === DataVisualizationType.PROFILING_CHART &&
    profileType === ProfileType.Velocity &&
    measure.type === "Exercise" &&
    measure.metadata.repetitions.length
  );

  const variants = measure.type === "Exercise" ? measure.metadata.variants : undefined;

  const { data: latestLvpData, status: latestLvpStatus } = useGetLatestLoadVelocityProfilesQuery(
    { athleteId, exerciseId, variants },
    { enabled: enableLvpApi }
  );

  // Drop selected profiles once they stop being offered (e.g. on the power profile), so they don't return with them,
  // and keep the rest pointing at the fresh instances a refetch brings. An effect rather than the profile type
  // handler: the offered profiles also change through the URL, the exercise, the athlete and the chart type.
  useEffect(() => {
    setSelectedProfiles((profiles) => LoadVelocityProfileHelpers.reconcile(profiles, latestLvpData));
  }, [latestLvpData]);

  const contextValue = useMemo(
    () => ({
      value: LoadVelocityProfileHelpers.reconcile(selectedProfiles, latestLvpData),
      options: latestLvpData,
      onChange: (profiles: LoadVelocityProfile[]) => {
        setSelectedProfiles(profiles);
      },
    }),
    [latestLvpData, selectedProfiles]
  );

  return (
    <ChartProfileSelectStatusContext.Provider value={latestLvpStatus}>
      <ChartProfileSelectContext.Provider value={contextValue}>{props.children}</ChartProfileSelectContext.Provider>
    </ChartProfileSelectStatusContext.Provider>
  );
}

export default ChartProfileSelectProvider;
